import React, { useState, useEffect } from 'react';
import { tratamientosService } from '../api/tratamientos';
import ResponsiveTable from '../components/common/ResponsiveTable';
import AddButton from '../components/ui/AddButton';

const TratamientosPage = () => {
  const [tratamientos, setTratamientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTratamiento, setEditingTratamiento] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('success');
  const [formError, setFormError] = useState('');

  const [formData, setFormData] = useState({
    nombre: '',
    precio: '',
    duracion_minutos: '',
    descripcion: ''
  });

  useEffect(() => {
    cargarTratamientos();
  }, []);

  const cargarTratamientos = async () => {
    try {
      const response = await tratamientosService.getAll();
      const data = Array.isArray(response.data) ? response.data : [];
      setTratamientos(data);
    } catch (error) {
      console.error('Error al cargar tratamientos:', error);
      setTratamientos([]);
      showAlertMessage('Error al cargar los tratamientos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showAlertMessage = (message, type = 'success') => {
    setAlertMessage(message);
    setAlertType(type);
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 3000);
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '-';
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const handleOpenModal = (tratamiento = null) => {
    if (tratamiento) {
      setEditingTratamiento(tratamiento);
      setFormData({
        nombre: tratamiento.nombre || '',
        precio: tratamiento.precio?.toString() || '',
        duracion_minutos: tratamiento.duracion_minutos?.toString() || '',
        descripcion: tratamiento.descripcion || ''
      });
    } else {
      setEditingTratamiento(null);
      setFormData({
        nombre: '',
        precio: '',
        duracion_minutos: '60',
        descripcion: ''
      });
    }
    setFormError('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTratamiento(null);
    setFormError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    // Validaciones
    if (!formData.nombre.trim()) {
      setFormError('El nombre es obligatorio');
      return;
    }
    if (!formData.precio || parseFloat(formData.precio) < 0) {
      setFormError('El precio debe ser un valor válido');
      return;
    }
    if (!formData.duracion_minutos || parseInt(formData.duracion_minutos) <= 0) {
      setFormError('La duración debe ser mayor a 0 minutos');
      return;
    }

    try {
      const tratamientoData = {
        nombre: formData.nombre.trim(),
        precio: parseFloat(formData.precio),
        duracion_minutos: parseInt(formData.duracion_minutos),
        descripcion: formData.descripcion.trim() || null
      };

      if (editingTratamiento) {
        await tratamientosService.update(editingTratamiento.id, tratamientoData);
        showAlertMessage('Tratamiento actualizado correctamente');
      } else {
        await tratamientosService.create(tratamientoData);
        showAlertMessage('Tratamiento creado correctamente');
      }

      handleCloseModal();
      await cargarTratamientos();
    } catch (error) {
      console.error('Error al guardar tratamiento:', error);
      const errorMsg = error.response?.data?.detail || 
                       error.response?.data?.message ||
                       Object.values(error.response?.data || {}).flat().join(', ') ||
                       'Error al guardar el tratamiento';
      setFormError(errorMsg);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Está seguro de eliminar este tratamiento? Esta acción no se puede deshacer.')) {
      try {
        await tratamientosService.delete(id);
        showAlertMessage('Tratamiento eliminado correctamente');
        await cargarTratamientos();
      } catch (error) {
        console.error('Error al eliminar tratamiento:', error);
        const errorMsg = error.response?.data?.detail || 
                         'No se puede eliminar el tratamiento. Puede estar siendo usado en citas existentes.';
        showAlertMessage(errorMsg, 'error');
      }
    }
  };

  // Columnas para la tabla
  const columns = [
    {
      key: 'nombre',
      label: 'Nombre',
      render: (value) => (
        <div className="text-sm font-medium text-gray-900">
          {value}
        </div>
      )
    },
    {
      key: 'precio',
      label: 'Precio',
      render: (value) => (
        <div className="text-sm font-semibold text-green-600">
          {formatCurrency(value)}
        </div>
      )
    },
    {
      key: 'duracion_minutos',
      label: 'Duración',
      render: (value) => (
        <div className="text-sm text-gray-700">
          {value} min
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Alerta */}
      {showAlert && (
        <div className={`mb-4 p-4 ${alertType === 'error' ? 'bg-red-100 border-red-400 text-red-700' : 'bg-green-100 border-green-400 text-green-700'} rounded relative`}>
          <span className="block sm:inline">{alertMessage}</span>
          <button
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
            onClick={() => setShowAlert(false)}
          >
            <span className="sr-only">Cerrar</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Encabezado */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Tratamientos</h1>
          <p className="text-sm text-gray-500 mt-1">Gestiona el catálogo de servicios de tu clínica</p>
        </div>
        <AddButton onClick={() => handleOpenModal()}>
          Nuevo Tratamiento
        </AddButton>
      </div>

      {/* Contador */}
      <div className="mb-4 text-sm text-gray-600">
        {tratamientos.length} tratamiento{tratamientos.length !== 1 ? 's' : ''} registrado{tratamientos.length !== 1 ? 's' : ''}
      </div>

      {/* Tabla */}
      <ResponsiveTable
        data={tratamientos}
        columns={columns}
        onEdit={handleOpenModal}
        onDelete={(item) => handleDelete(item.id)}
        emptyMessage="No hay tratamientos registrados. Crea tu primer servicio."
        keyField="id"
      />

      {/* Modal de Formulario */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center" onClick={(e) => e.target === e.currentTarget && handleCloseModal()}>
          <div className="relative mx-4 p-6 border w-full max-w-md shadow-lg rounded-lg bg-white">
            {/* Header del modal */}
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingTratamiento ? 'Editar Tratamiento' : 'Nuevo Tratamiento'}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Error del formulario */}
            {formError && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
                {formError}
              </div>
            )}

            {/* Formulario */}
            <form onSubmit={handleSubmit}>
              {/* Nombre */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre del Tratamiento <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder="Ej: Podología General, Manicura..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                  required
                />
              </div>

              {/* Precio y Duración en grid */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                {/* Precio */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Precio <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      value={formData.precio}
                      onChange={(e) => setFormData({ ...formData, precio: e.target.value })}
                      placeholder="15000"
                      min="0"
                      step="100"
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                      required
                    />
                  </div>
                </div>

                {/* Duración */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Duración (min) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.duracion_minutos}
                    onChange={(e) => setFormData({ ...formData, duracion_minutos: e.target.value })}
                    placeholder="60"
                    min="1"
                    max="480"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                    required
                  />
                </div>
              </div>

              {/* Descripción */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción <span className="text-gray-400">(opcional)</span>
                </label>
                <textarea
                  value={formData.descripcion}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  placeholder="Describe brevemente el tratamiento..."
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors resize-none"
                />
              </div>

              {/* Botones */}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-[#2a9d8f] hover:bg-[#23877a] rounded-lg transition-colors"
                >
                  {editingTratamiento ? 'Guardar Cambios' : 'Crear Tratamiento'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TratamientosPage;
